import { JobSpeciality, getJobSpecialityLabel, isDualPlusPublicSafetySpeciality } from '@clipr/lib';
import { ReactNode } from 'react';
import { DropdownItemObject } from '../../components/input/dropdownInput';
import { JobModel } from '../job';

export { getJobSpecialityLabel, isDualPlusPublicSafetySpeciality };

type JobSpecialityOutput = 'PRIMARYCARE' | undefined;

export const Specialities: JobSpeciality[] = [
  'Standard',
  'Medical',
  'PublicSafety',
  'PatrolAiSuggestions',
  'FirstDraft',
  'InterviewRoom',
  'FraudInvestigation',
  'RedactionAsAService',
  'SelfServiceBWCRedaction',
  'PsychEval',
  'PEPPreEmploymentInterview',
  'InternalAffairsInterviewFirstDraft',
  'ComplianceBehavioralAnalyst',
  'InsuranceLossAdjuster',
  'QuoteToEstimate',
  'SiteWalk',
  'QualityOfLifeCompliance',
  'DepartureLog',
];

export const getJobSpecialityTooltip = (speciality: JobSpeciality): ReactNode => {
  switch (speciality) {
    case 'PublicSafety':
      return 'First patrol report draft';
    case 'PatrolAiSuggestions':
      return 'AI review for patrol drafts';
    case 'FirstDraft':
      return 'General first-pass draft';
    case 'InterviewRoom':
      return 'Draft for interviews and statements';
    case 'FraudInvestigation':
      return 'Draft for fraud cases';
    case 'RedactionAsAService':
      return 'Managed redaction service';
    case 'SelfServiceBWCRedaction':
      return 'Self-serve body cam redaction';
    case 'PsychEval':
      return 'Draft for psych evaluations';
    case 'PEPPreEmploymentInterview':
      return 'Draft for pre-employment interviews';
    case 'ComplianceBehavioralAnalyst':
      return 'Compliance and behavior analysis';
    case 'DocumentTranslation':
      return 'Document translation output';
    case 'WarrantFirstDraft':
      return 'First warrant draft';
    case 'SubpoenaRequestDraft':
      return 'Subpoena request draft';
    case 'InternalAffairsInterviewFirstDraft':
      return 'First internal affairs interview draft';
    case 'InsuranceLossAdjuster':
      return 'Draft for insurance loss adjusting visits';
    case 'QuoteToEstimate':
      return 'Quote drafted from a site walkthrough';
    case 'SiteWalk':
      return 'No-price facility data sheet from a site walk';
    case 'QualityOfLifeCompliance':
      return 'Officer courtesy scorecard from BWC footage';
    case 'DepartureLog':
      return 'Airline departure record from a coordinator\'s narration';
    case 'Custom':
      return 'Custom-trained report format';
    case 'Medical':
      return 'Medical documentation draft';
    case 'Standard':
      return 'Standard report format';
    default: return null;
  }
}

export const getDefaultJobSpeciality = (): JobSpeciality => {
  return 'FirstDraft';
}

export const getJobSpecialityInputLabel = (): string => {
  return 'Job Speciality';
}

export const SpecialityItems: DropdownItemObject[] = Specialities
  .map(item => ({ value: item, label: getJobSpecialityLabel(item), tooltip: getJobSpecialityTooltip(item) }));

export const FilteredSpecialityItems: DropdownItemObject[] = SpecialityItems.filter((item) => item.value !== 'PublicSafety');

export const DefaultSpecialityItem: DropdownItemObject | null = SpecialityItems.find(({ value }) => value === getDefaultJobSpeciality()) || null;

export const getJobSpecialityOutput = (speciality: JobSpeciality | null): JobSpecialityOutput => {
  return speciality === 'Medical' ? 'PRIMARYCARE' : undefined;
}

export const getJobSpecialityInput = (job: JobModel | null): JobSpeciality | null => {
  if (!job?.hasTranscriptLevel)
    return null;

  if (job.medicalSpecialty === 'PRIMARYCARE') {
    return 'Medical';
  }

  return job.speciality;
}

export const CUSTOM_SPECIALTY_PREFIX = 'Custom:';

export interface CustomSpecialtyDropdownItem extends DropdownItemObject {
  customSpecialtyId: string;
}

export function getSpecialityItemsWithCustom(
  customSpecialties: Array<{ id: string; name: string }>,
): Array<DropdownItemObject | CustomSpecialtyDropdownItem> {
  if (!customSpecialties.length) return SpecialityItems;

  const customItems: CustomSpecialtyDropdownItem[] = customSpecialties.map(cs => ({
    value: `${CUSTOM_SPECIALTY_PREFIX}${cs.id}`,
    label: cs.name,
    customSpecialtyId: cs.id,
    tooltip: getJobSpecialityTooltip('Custom' as JobSpeciality),
  }));

  return [...SpecialityItems, ...customItems];
}

export function parseCustomSpeciality(value: string | null | undefined): {
  speciality: JobSpeciality;
  customSpecialtyId: string;
} | null {
  if (!value?.startsWith(CUSTOM_SPECIALTY_PREFIX)) return null;
  return {
    speciality: 'Custom' as JobSpeciality,
    customSpecialtyId: value.slice(CUSTOM_SPECIALTY_PREFIX.length),
  };
}

export function getTeamDefaultJobSpecialityInput(
  team: {
    defaultJobSpeciality?: JobSpeciality | null;
    defaultCustomSpecialtyId?: string | null;
  } | null | undefined,
  customSpecialties: Array<{ id: string; name: string }>,
): JobSpeciality | string | null {
  if (!team?.defaultJobSpeciality) return null;

  if (team.defaultJobSpeciality !== 'Custom') return team.defaultJobSpeciality;

  if (
    team.defaultCustomSpecialtyId &&
    customSpecialties.some(customSpecialty => customSpecialty.id === team.defaultCustomSpecialtyId)
  ) {
    return `${CUSTOM_SPECIALTY_PREFIX}${team.defaultCustomSpecialtyId}`;
  }

  if (!team.defaultCustomSpecialtyId && customSpecialties.length === 1) {
    return `${CUSTOM_SPECIALTY_PREFIX}${customSpecialties[0].id}`;
  }

  return DefaultSpecialityItem?.value ?? null;
}
